/**
 * Email recipients as chips with a type-and-Enter input — the task-label
 * multi-select pattern (see `TaskLabelsEditor`), inlined for the routine
 * delivery surfaces: the chat channel card and the routines-page delivery
 * editor. Enter (or comma, or blur) adds the typed address as a chip; each
 * chip has an X to remove it; an address the server would refuse is flagged
 * while it is being typed instead of failing the save.
 *
 * Deliberately NOT ui-core's `TagInput`: that component is a label + input +
 * add-button section with badges below and toast errors — a different shape
 * from the inline chips-in-field pattern this design specifies.
 */

import React from 'react';
import { X } from 'lucide-react';
import { cn } from '@strivelabs/ui-core';

/**
 * Mirrors `EMAIL_PATTERN` in `apps/server/src/notification/preference_service.ts`,
 * so an address the server would refuse is caught while it is being typed.
 */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Whether a value is an email address the platform will accept. Shared by the
 * chip input and the surfaces that seed it (the chat card, the delivery
 * editor), so a malformed stored/LLM-supplied address is filtered at the seam
 * instead of aborting the save later.
 */
export function isValidEmail(value: unknown): value is string {
  return typeof value === 'string' && EMAIL_PATTERN.test(value);
}

export interface EmailRecipientsInputProps {
  recipients: string[];
  onChange: (recipients: string[]) => void;
  disabled?: boolean;
}

export function EmailRecipientsInput({
  recipients,
  onChange,
  disabled = false,
}: EmailRecipientsInputProps) {
  const [query, setQuery] = React.useState('');

  const typed = query.trim().toLowerCase();
  const valid = EMAIL_PATTERN.test(typed);
  // Case-insensitive: seeded recipients keep their stored casing, and
  // "Ops@Acme.com" and "ops@acme.com" are the same mailbox — a second chip
  // would double every alert for that person.
  const duplicate = recipients.some((each) => each.toLowerCase() === typed);

  const add = () => {
    if (!typed || !valid || duplicate) return;
    onChange([...recipients, typed]);
    setQuery('');
  };

  const remove = (address: string) => {
    onChange(recipients.filter((each) => each !== address));
  };

  return (
    <div className="flex min-w-[min(240px,100%)] flex-1 flex-col gap-1">
      <div
        className={cn(
          'flex min-h-8 flex-wrap items-center gap-1.5 rounded-md border border-custom-border bg-white px-2 py-1',
          disabled && 'pointer-events-none opacity-60'
        )}
      >
        {recipients.map((address) => (
          <span
            key={address}
            className="inline-flex items-center gap-1 rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-900"
          >
            {address}
            <button
              type="button"
              aria-label={`Remove ${address}`}
              className="inline-flex h-4 w-4 items-center justify-center rounded text-gray-400 transition-colors hover:text-gray-900"
              onClick={() => remove(address)}
            >
              <X className="size-3" aria-hidden />
            </button>
          </span>
        ))}
        <input
          value={query}
          disabled={disabled}
          aria-label="Add email recipient"
          placeholder={recipients.length === 0 ? 'name@company.com' : 'Add another…'}
          className="min-w-[140px] flex-1 border-0 bg-transparent py-0.5 text-sm text-gray-900 outline-none placeholder:text-gray-400"
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ',') {
              event.preventDefault();
              add();
            }
            // Backspace on an empty input removes the last chip — standard
            // multi-select affordance.
            if (event.key === 'Backspace' && query === '' && recipients.length > 0) {
              remove(recipients[recipients.length - 1]);
            }
          }}
          onBlur={add}
        />
      </div>
      {typed && !valid ? (
        <p className="text-xs text-gray-400">
          Press Enter after a full address, like name@company.com
        </p>
      ) : null}
    </div>
  );
}
